import { env } from '@config/env';
import { scrub } from './scrub';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogContext = Readonly<Record<string, unknown>>;

export interface LogEntry {
  readonly level: LogLevel;
  readonly message: string;
  readonly context?: LogContext;
  readonly timestamp: string;
}

export interface LogSink {
  write(entry: LogEntry): void;
}

class ConsoleSink implements LogSink {
  write(entry: LogEntry): void {
    const prefix = `[${entry.level.toUpperCase()}] ${entry.timestamp} ${entry.message}`;
    const fn =
      entry.level === 'error'
        ? console.error
        : entry.level === 'warn'
          ? console.warn
          // eslint-disable-next-line no-console -- this IS the structured logger
          : console.log;
    if (entry.context !== undefined) {
      fn(prefix, entry.context);
    } else {
      fn(prefix);
    }
  }
}

class NoopSink implements LogSink {
  write(): void {
    // intentionally empty — used as the production default until a real sink is registered
  }
}

const sinks: LogSink[] = env.VITE_APP_ENV === 'production' ? [new NoopSink()] : [new ConsoleSink()];

function emit(level: LogLevel, message: string, context?: LogContext): void {
  const timestamp = new Date().toISOString();
  const entry: LogEntry =
    context !== undefined
      ? { level, message, context: scrub(context), timestamp }
      : { level, message, timestamp };
  for (const sink of sinks) sink.write(entry);
}

export const logger = {
  debug: (message: string, context?: LogContext): void => {
    emit('debug', message, context);
  },
  info: (message: string, context?: LogContext): void => {
    emit('info', message, context);
  },
  warn: (message: string, context?: LogContext): void => {
    emit('warn', message, context);
  },
  error: (message: string, context?: LogContext): void => {
    emit('error', message, context);
  },
} as const;

/**
 * Register an additional sink (e.g. Datadog, Sentry) — wired in main.tsx
 * behind a feature flag. The sink contract is intentionally minimal so we
 * can't accidentally couple to any vendor.
 */
export function registerSink(sink: LogSink): void {
  sinks.push(sink);
}

/** Test-only: clear all sinks. Do not call from app code. */
export function __resetSinks(): void {
  sinks.length = 0;
}
